import LfDataService from '../../../Services/LfDataService';
import LfWebService from '../../../Services/LfWebService';
import AnalyticsService from '../../../Services/AnalyticsService';

import {
    pageIsLoadingAction,
    pageCurrentStepAction,
    pageHasErroredAction,
    pageErrorAction
} from '../../../State/actions';

import {sortInsurancesByName, setAnswersRecursively} from '../../../DataHelpers/lfDataHelpers';

// constants

import {repeatableIdSeparator} from '../../../DynamicForms/constants';



// page step switch hook

export function pensionGuidanceCurrentStepAction(step) {
    return (dispatch, getState) => {
        dispatch(pageCurrentStepAction(step));
    };
}

// bootstrap form data and page

export function loadPensionGuidanceDataAction() {
    return (dispatch) => {
        const lfDataService = new LfDataService();
        const lfWebService = new LfWebService();

        dispatch(pageIsLoadingAction(true));

        Promise.all([
            lfDataService.pensionGuidanceForm(),
            lfDataService.customerInfo()
        ]).then(values => {
            let rawData = values.reduce((acc, obj) => {
                Object.keys(obj).forEach(prop => acc[prop] = obj[prop]);
                return acc;
            }, {});

            let {questionGroups, ...data} = rawData;
 
            if (questionGroups && questionGroups.length) {
                questionGroups.forEach((group, index) => {
                    questionGroups[index] = setAnswersRecursively(group);
                })
            }

            // add base level form inputs from groups
            dispatch(addQuestionGroupsQuestionsAction(questionGroups));

            // data
            data.pensionGuidanceFormData = {questionGroups};
            data.myPagesStartUrl = lfWebService.myPagesStartUrl;
            data.insurancePurchaseUrl = lfWebService.insurancePurchaseUrl();

            dispatch(pensionGuidanceDataLoadedAction(data));
            dispatch(pageIsLoadingAction(false));
            dispatch(pensionGuidanceCurrentStepAction(0));
        })
        .catch(e => {
            dispatch(pageErrorAction(e));
            dispatch(pageHasErroredAction(true));

            if (window._bazDebug) {
                throw e;
            }
        });
    };
}

export function pensionGuidanceDataLoadedAction(data) {
    return {
        type: 'PENSION_GUIDANCE_DECLARATION_DATA_LOADED',
        pensionGuidancePageData: data
    };
}


// upload validated declaration data

export function savePensionGuidanceAnswersAction() {
    const lfDataService = new LfDataService();
    const lfWebService = new LfWebService();

    return (dispatch, getState) => {
        const {
            pageCurrentStep,
            pensionGuidancePageData,
            pensionGuidanceFormInputs
        } = getState();

        const personId = pensionGuidancePageData.customer.personId;
        const answers = Object.keys(pensionGuidanceFormInputs)
            .filter(k =>
                pensionGuidanceFormInputs[k].valid
                    && pensionGuidanceFormInputs[k].postable
            )
            .map(k => ({
                questionId: k,
                answer: pensionGuidanceFormInputs[k].value,

                // parse repetition count from id:
                // normal question id: '14dc0a49e0f44453b2d24feb9b4106e0'
                // first repetition id: '14dc0a49e0f44453b2d24feb9b4106e0__1'
                // the first repetition of the question (the second occurence)
                // will have an index-suffixed id

                repetition: k.indexOf(repeatableIdSeparator) > -1
                    ? parseInt(k.slice(k.indexOf(repeatableIdSeparator) + repeatableIdSeparator.length, k.length))
                    : undefined
            }));

        const postData = {
            personId,
            answers: answers
        };

        lfDataService.submitPensionGuidance(postData)
            .then(data => {
                if(!data.success) {
                    throw 'det gick inte att skicka in dina svar - var vänlig försök igen senare';
                    return;
                }

                dispatch(pensionGuidanceCurrentStepAction(pageCurrentStep + 1));
                dispatch(pageIsLoadingAction(false));
            })
            .catch(error => {
                dispatch(pageErrorAction(error));
                dispatch(pageHasErroredAction(true));
            });
    };
}

// form handling

export function addQuestionGroupsQuestionsAction(questionGroup, nestLev = 0, maxLev = 2) {
    return (dispatch, getState) => {
        const { pensionGuidanceFormInputs } = getState();

        const addInputs = (questionGroup, nestLevel, maxLevel, ignoreNestLevel = false) => {
            if (!ignoreNestLevel && nestLevel === maxLevel) {
                return;
            }

            questionGroup.forEach(group => {
                group.questions.forEach(question => {
                    if (!(question.id in pensionGuidanceFormInputs)) {
                        dispatch(initPensionGuidanceFormInputAction(question));
                        dispatch(setPensionGuidanceFormInputActiveStatusAction(question.id, !question.isUneditable));

                        if (question.subQuestionGroup) {
                            const toggleGroupOnValue = question.questionType === 'Radio'
                                ? question.options
                                    .filter(option => option.togglesSubQuestionGroup)
                                    .reduce((_, option) => option.text, null)
                                : null;

                            if (question.value === toggleGroupOnValue || question.questionType !== 'Radio') {
                                addInputs([question.subQuestionGroup], nestLevel + 1, maxLevel);
                            }
                        }
                    }
                });

                if (group.subQuestionGroups && group.subQuestionGroups.length) {
                    addInputs(group.subQuestionGroups, nestLevel + 1, maxLevel);
                }
            });
        };

        addInputs(questionGroup, nestLev, maxLev);
    };
}

export function removeQuestionGroupsQuestionsAction(questionGroup, nestLev = 0, maxLev = 2) {
    return (dispatch) => {
        const removeInputs = (questionGroup, nestLevel, maxLevel) => {
            if (nestLevel === maxLevel) {
                return;
            }

            questionGroup.forEach(group => {
                group.questions.forEach(question => {
                    dispatch(removePensionGuidanceFormInputAction(question.id));

                    if (question.subQuestionGroup) {
                        removeInputs([question.subQuestionGroup], nestLevel + 1, maxLevel);
                    }
                });

                if (group.subQuestionGroups.length) {
                    removeInputs(group.subQuestionGroups, nestLevel + 1, maxLevel);
                }
            });
        };

        removeInputs(questionGroup, nestLev, maxLev);
    };
}

export function repeatQuestionGroupAction(questionGroup) {
    const questionGroupId = questionGroup.id;

    return (dispatch, getState) => {
        const {pensionGuidanceRepeatedInputs} = getState();
        const currentRepeatCount = questionGroupId in pensionGuidanceRepeatedInputs
            ? pensionGuidanceRepeatedInputs[questionGroupId] + 1
            : 1;

        questionGroup.questions.forEach(question => {
            let repeatedQuestion = {
                ...question,
                id:  `${question.id}${repeatableIdSeparator}${currentRepeatCount}`
            }

            dispatch(initPensionGuidanceFormInputAction(repeatedQuestion, true));
        });

        dispatch(pensionGuidanceGroupRepeatedAction(questionGroupId));
    }
}

export function removeRepeatedQuestionGroupAction(questionGroup, repeatableCountIndex) {
    const questionGroupId = questionGroup.id;
    const questionIds = questionGroup.questions.map(q => q.id);
    
    return (dispatch, getState) => {
        questionIds.forEach(questionId => {
            dispatch(removePensionGuidanceFormInputAction(`${questionId}${repeatableIdSeparator}${repeatableCountIndex}`))
        });
        dispatch(pensionGuidanceGroupRemoveRepeatedAction(questionGroupId));
        dispatch(reorderRepeatedQuestionFormInputsAction(questionGroup));
    }
}

export function reorderRepeatedQuestionFormInputsAction(questionGroup) {
    return (dispatch, getState) => {
        const {pensionGuidanceFormInputs, pensionGuidanceRepeatedInputs} = getState();

        const questionGroupId = questionGroup.id;
        const repeatableCounter = pensionGuidanceRepeatedInputs[questionGroupId];
        const questionIds = questionGroup.questions.map(q => q.id);
        
        const groupInputFormsKeys = {};
        const groupInputFormsValues = {};
        questionIds.forEach(questionId => {
            groupInputFormsKeys[questionId] = [];
            groupInputFormsValues[questionId] = [];
            
            Object.keys(pensionGuidanceFormInputs).forEach(function(key, index) {
                const questionIdKeyPrefix = `${questionId}${repeatableIdSeparator}`;
                if (key.startsWith(questionIdKeyPrefix)) {
                    groupInputFormsKeys[questionId].push(key);
                    groupInputFormsValues[questionId].push(pensionGuidanceFormInputs[key]);
                }
            });

            groupInputFormsKeys[questionId].forEach(key => {
                dispatch(removePensionGuidanceFormInputAction(key));
                delete pensionGuidanceFormInputs[key];
            });

            groupInputFormsKeys[questionId].sort();

            groupInputFormsKeys[questionId].forEach((repeatedQuestionId, index) => {
                const value = groupInputFormsValues[questionId].find(q => q.id === repeatedQuestionId);
                const repeatableIndex = index + 1;
                const newKey = `${questionId}${repeatableIdSeparator}${repeatableIndex}`
                value.id = newKey;

                dispatch(initPensionGuidanceFormInputAction(value));
            });
        });
    }
}

export function pensionGuidanceGroupRemoveRepeatedAction(groupId) {
    return {
        type: 'PENSION_GUIDANCE_REMOVE_REPEAT_FORM_INPUT',
        groupId: groupId
    };
}

export function pensionGuidanceGroupRepeatedAction(groupId) {
    return {
        type: 'PENSION_GUIDANCE_REPEAT_FORM_INPUT',
        groupId: groupId
    };
}


export function pensionGuidanceFormDataUpdatedAction(data) {
    return {
        type: 'PENSION_GUIDANCE_FORM_DATA_UPDATED',
        pensionGuidanceFormData: data
    };
}

export function initPensionGuidanceFormInputAction(question, isActive = false) {
    return (dispatch) => {
        const hasValue = typeof question.value !== 'undefined' && question.value;
        const isUneditable = question.isUneditable;
        const isRequired = question.required || question.questionType == 'Radio';

        dispatch(updatePensionGuidanceFormInputAction({
            id: question.id,
            questionText: question.questionText,
            value: hasValue ? question.value : '',
            required: isRequired,
            active: isActive,
            valid: hasValue || !isRequired || isUneditable ? true : false,
            touched: hasValue ? true : false,
            postable: !isUneditable
        }));
    };
}

export function removePensionGuidanceFormInputAction(questionId) {
    return {
        type: 'PENSION_GUIDANCE_FORM_INPUT_REMOVED',
        questionId
    };
}

export function setPensionGuidanceFormInputActiveStatusAction(questionId, status) {
    return (dispatch, getState) => {
        const { pensionGuidanceFormInputs } = getState();

        dispatch(updatePensionGuidanceFormInputAction({
            ...pensionGuidanceFormInputs[questionId],
            active: status
        }));
    };
}

export function pensionGuidanceFormInputChangeAction(questionId, value, validator) {
    return (dispatch, getState) => {
        const { pensionGuidanceFormInputs } = getState();
        const currentInput = pensionGuidanceFormInputs[questionId];

        dispatch(updatePensionGuidanceFormInputAction({
            ...currentInput,
            value: value,
            active: true
        }));

        dispatch(validatePensionGuidanceFormInputAction(questionId, validator, currentInput.touched));
    };
}

export function updatePensionGuidanceFormInputAction(inputData) {
    return {
        type: 'PENSION_GUIDANCE_FORM_INPUT_UPDATED',
        inputData
    };
}

export function validatePensionGuidanceFormInputAction(questionId, validator, touched = true) {
    return (dispatch, getState) => {
        const { pensionGuidanceFormInputs } = getState();
        const currentInput = pensionGuidanceFormInputs[questionId];
        const questionValue = currentInput.value;

        let valid = true;

        if (typeof validator === 'function'
            && currentInput.value != null
            && currentInput.value != '')
        {
            valid = typeof validator(questionValue) === 'undefined'; // see rules.jsx
        } else if (currentInput.required) {
            valid = questionValue != null && questionValue !== '';
        }

        dispatch(updatePensionGuidanceFormInputAction({
            ...currentInput,
            touched,
            valid
        }));

        dispatch(updatePensionGuidanceStatusAction()); // update global valid/invalid form status
    };
}

export function showAllPensionGuidanceFormErrorsAction() {
    return (dispatch, getState) => {
        const { pensionGuidanceFormInputs } = getState();

        Object.keys(pensionGuidanceFormInputs).forEach(questionId => {
            const currentInput = pensionGuidanceFormInputs[questionId];

            if (currentInput.active && !currentInput.touched) {
                dispatch(updatePensionGuidanceFormInputAction({
                    ...currentInput,
                    touched: true
                }));
            }
        });
    };
}

export function updatePensionGuidanceStatusAction() {
    return (dispatch, getState) => {
        const {pensionGuidanceFormInputs} = getState();

        let invalidInputs = Object.keys(pensionGuidanceFormInputs)
            .map(key => pensionGuidanceFormInputs[key])
            .filter(input => input.active && !input.valid);

        dispatch(setPensionGuidanceStatusAction(invalidInputs.length == 0));
    };
}

export function setPensionGuidanceStatusAction(bool) {
    return {
        type: 'PENSION_GUIDANCE_FORM_STATUS',
        valid: bool
    };
}
